import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ─── Auth ──────────────────────────────────────────────────────────────
export const authAPI = {
  // Direct login: Email + Password → JWT (no OTP)
  directLogin:       (data) => api.post('/auth/login', data),

  // Direct registration (no OTP, legacy)
  directRegister:    (data) => api.post('/auth/register', data),

  // Registration with OTP (preferred)
  sendRegisterOtp:   (data) => api.post('/auth/register/send-otp', data),
  verifyRegisterOtp: (data) => api.post('/auth/register/verify-otp', data),

  // OTP login (legacy, kept for standalone LoginPage)
  sendLoginOtp:      (data) => api.post('/auth/login/send-otp', data),
  verifyLoginOtp:    (data) => api.post('/auth/login/verify-otp', data),

  // Forgot Password
  sendForgotOtp:     (data) => api.post('/auth/forgot-password/send-otp', data),
  verifyForgotOtp:   (data) => api.post('/auth/forgot-password/verify-otp', data),
  resetPassword:     (data) => api.post('/auth/forgot-password/reset', data),

  me: () => api.get('/auth/me'),
};

// ─── Profile ───────────────────────────────────────────────────────────
export const profileAPI = {
  getProfile:      ()         => api.get('/profile'),
  updateProfile:   (data)     => api.put('/profile', data),
  updatePhoto:     (photoUrl) => api.patch('/profile/photo', { photoUrl }),
  changePassword:  (data)     => api.post('/profile/change-password', data),
  getActivity:     ()         => api.get('/profile/activity'),
  deleteAccount:   (password) => api.delete('/profile', { data: { password } }),
};

// ─── Events ────────────────────────────────────────────────────────────
export const eventsAPI = {
  getEvents:   (params) => api.get('/events', { params }),
  getEvent:    (id)     => api.get(`/events/${id}`),
  getDashboard: ()      => api.get('/events/dashboard'),
  refresh:     ()       => api.post('/events/refresh'),
};

// ─── Alerts ────────────────────────────────────────────────────────────
export const alertsAPI = {
  getAlerts:    ()         => api.get('/alerts'),
  createAlert:  (data)     => api.post('/alerts', data),
  updateAlert:  (id, data) => api.put(`/alerts/${id}`, data),
  deleteAlert:  (id)       => api.delete(`/alerts/${id}`),
  toggleAlert:  (id)       => api.patch(`/alerts/${id}/toggle`),
  getHistory:   ()         => api.get('/alerts/history'),
};

// ─── Admin ─────────────────────────────────────────────────────────────
export const adminAPI = {
  triggerFetch:           ()           => api.post('/admin/fetch-nasa-data'),
  getUsers:               ()           => api.get('/admin/users'),
  updateBriefingSelection:(selectedIds) => api.put('/admin/users/briefing-selection', { selectedIds }),
  getBriefingStatus:      ()           => api.get('/admin/briefing/status'),
};

export default api;

// ─── Solar System ──────────────────────────────────────────────────────
export const solarSystemAPI = {
  getPlanets:    ()     => api.get('/solar-system/planets'),
  getPlanet:     (name) => api.get(`/solar-system/planets/${name}`),
};
// ─── NASA Exoplanet Archive ────────────────────────────────────────────────
export const nasaExoplanetAPI = {
  getExoplanets:  (params) => api.get('/exoplanets', { params }),
  getHabitable:   ()       => api.get('/exoplanets/habitable'),
  getStats:       ()       => api.get('/exoplanets/stats'),
};

// ─── ISS / Open Notify ────────────────────────────────────────────────────
export const issAPI = {
  getPosition:   ()              => api.get('/iss/position'),
  getAstronauts: ()              => api.get('/iss/astronauts'),
  getPasses:     (lat, lon)      => api.get('/iss/passes', { params: { lat, lon } }),
};
// ─── Night Sky API ─────────────────────────────────────────────────────────
export const skyAPI = {
  getSkyData:    (lat, lon, date, hour) =>
    api.get('/sky/data', { params: { lat, lon, date, hour } }),
  getAPOD:       (date) => api.get('/sky/apod', { params: { date } }),
  getObjectInfo: (id, date) => api.get('/sky/object-info', { params: { id, date } }),
};
// ─── Study Zone ────────────────────────────────────────────────────────────
export const studyAPI = {
  getTheories: (category) => api.get('/study/theories', { params: { category } }),
  getTheory:   (id)       => api.get(`/study/theories/${id}`),
  getVideos:   (category) => api.get('/study/videos',   { params: { category } }),
};

// ─── Moon ──────────────────────────────────────────────────────────────────
export const moonAPI = {
  getData: (lat = 0, lon = 0) => api.get('/moon', { params: { lat, lon } }),
};

// ─── Space Weather Impact ──────────────────────────────────────────────────
export const spaceWeatherImpactAPI = {
  getImpact: () => api.get('/space-weather'),
  refresh:   () => api.post('/space-weather/refresh'),
};

// ─── Satellite Pass Alerts ─────────────────────────────────────────────────
export const satelliteAlertsAPI = {
  subscribe:         (data) => api.post('/alerts/subscribe', data),
  unsubscribe:       ()     => api.delete('/alerts/unsubscribe'),
  mySubscriptions:   ()     => api.get('/alerts/my-subscriptions'),
};